import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import Snackbar from "@mui/material/Snackbar";
import ToastCard from "../components/ToastCard";
import { toast } from "../utils/toast";

const ToastContext = createContext(null);

export const ToastProvider = ({ children }) => {
  const [activeToast, setActiveToast] = useState(null);
  const [open, setOpen] = useState(false);

  const handleClose = useCallback((event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  }, []);

  const handleExited = useCallback(() => {
    setActiveToast(null);
  }, []);

  const showToast = useCallback(({ message, title, severity = "info", duration = 4000, force = false }) => {
    toast.show({ message, title, severity, duration, force });
  }, []);

  useEffect(() => {
    const unsubscribe = toast.subscribe((newToast) => {
      setActiveToast(newToast);
      setOpen(true);
    });
    return unsubscribe;
  }, []);

  const value = {
    showToast,
    toast,
    closeToast: () => setOpen(false),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      {activeToast ? (
        <Snackbar
          key={activeToast.id}
          open={open}
          autoHideDuration={activeToast.duration || 4000}
          onClose={handleClose}
          TransitionProps={{ onExited: handleExited }}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          sx={{
            zIndex: 99999,
            top: { xs: 16, sm: 24 },
            right: { xs: 16, sm: 24 },
          }}
        >
          <ToastCard
            title={activeToast.title}
            message={activeToast.message}
            severity={activeToast.severity}
            duration={activeToast.duration || 4000}
            onClose={() => setOpen(false)}
          />
        </Snackbar>
      ) : null}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    // If used outside provider, fallback to the singleton toast utility
    return {
      showToast: toast.show.bind(toast),
      toast,
      closeToast: () => {},
    };
  }
  return context;
};

export default ToastContext;
